import type { ReactNode } from "react"
import { createContext, useCallback, useContext, useState } from "react"

/**
 * Context to coordinate which linked entity menu (Issue, Project, Tag) is open.
 * On mobile, without this coordination, multiple menus can be open simultaneously
 * because touch events don't trigger the same blur/focus behavior as desktop.
 * This context ensures only one menu is open at a time.
 * There's probably a better way to fix this, but this works for now.
 */

type MenuType = "issue" | "project" | "tag" | null

interface LinkedMenuContextValue {
	openMenu: MenuType
	setOpenMenu: (menu: MenuType) => void
	isMenuOpen: (menu: MenuType) => boolean
	onMenuOpenChange: (menu: MenuType) => (open: boolean) => void
}

const LinkedMenuContext = createContext<LinkedMenuContextValue | null>(null)

export function LinkedMenuProvider({ children }: { children: ReactNode }) {
	const [openMenu, setOpenMenu] = useState<MenuType>(null)

	const isMenuOpen = useCallback(
		(menu: MenuType) => openMenu === menu,
		[openMenu],
	)

	const onMenuOpenChange = useCallback(
		(menu: MenuType) => (open: boolean) => {
			setOpenMenu(open ? menu : null)
		},
		[],
	)

	return (
		<LinkedMenuContext.Provider
			value={{ openMenu, setOpenMenu, isMenuOpen, onMenuOpenChange }}
		>
			{children}
		</LinkedMenuContext.Provider>
	)
}

export function useLinkedMenu() {
	const context = useContext(LinkedMenuContext)
	if (!context) {
		throw new Error("useLinkedMenu must be used within a LinkedMenuProvider")
	}
	return context
}
